import React, { useState, useEffect } from 'react';
import { Form, Input, Upload, message, Switch } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { CategoryFormData } from '../../types/categories';
import { uploadSingleFile, deleteFileByUrl } from '../../services/uploadService';

interface CategoryFormProps {
  form: any;
  initialValues?: CategoryFormData;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ form, initialValues }) => {
  const [imageList, setImageList] = useState<string[]>(initialValues?.image ? [initialValues.image] : []);

  useEffect(() => {
    form.setFieldsValue({ image: imageList[0] || '' });
  }, [imageList, form]);

  useEffect(() => {
    // Reset imageList khi initialValues thay đổi
    if (initialValues?.image) {
      setImageList([initialValues.image]);
    } else {
      setImageList([]);
    }

    // Set all form values when initialValues change
    if (initialValues) {
      const formValues = {
        name: initialValues.name || '',
        description: initialValues.description || '',
        image: initialValues.image || '',
        is_visible: initialValues.is_visible ?? true,
        name_en: initialValues.translations?.find((t: any) => t.type === 'en')?.name || '',
        description_en: initialValues.translations?.find((t: any) => t.type === 'en')?.description || ''
      };
      form.setFieldsValue(formValues);
    }
  }, [initialValues, form]);

  const handleUpload = async ({ file, fileList }: any) => {
    try {
      if (file.status === 'uploading') {
        const response = await uploadSingleFile(file.originFileObj);
        if (response.status === 'success') {
          const updatedFileList = fileList.map((f: any) => {
            if (f.uid === file.uid) {
              return {
                ...f,
                url: response.data.file.url,
                thumbUrl: response.data.file.url,
                status: 'done'
              };
            }
            return f;
          });
          setImageList(updatedFileList.map((f: any) => f.url));
        }
      } else if (file.status === 'removed') {
        try {
          const response = await deleteFileByUrl(file.url);
          if (response.status === 'success') {
            const remainingFiles = fileList.filter((f: any) => f.uid !== file.uid);
            setImageList(remainingFiles.map((f: any) => f.url));
            message.success('File deleted successfully');
          } else {
            message.error('Failed to delete file');
          }
        } catch (error) {
          console.error('Delete failed:', error);
          message.error('Failed to delete file');
          setImageList(imageList.filter((url: string) => url !== file.url));
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
      message.error('Upload failed');
    }
  };

  const handleRemove = async (file: any) => {
    try {
      const response = await deleteFileByUrl(file.url);
      if (response.status === 'success') {
        const newImageList = imageList.filter((url: string) => url !== file.url);
        setImageList(newImageList);
        message.success('File deleted successfully');
      } else {
        message.error('Failed to delete file');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      message.error('Failed to delete file');
      setImageList([]);
    }
    return true;
  };

  const uploadButton = (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );


  return (
    <Form
      form={form}
      layout='vertical'
      name='categoryForm'
      initialValues={initialValues || { is_visible: true }}
    >
      <Form.Item
        name='name'
        label='Tên danh mục'
        rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
      >
        <Input 
          placeholder='Nhập tên danh mục' 
          style={{
            width: '100%',
            height: 32,
            borderRadius: 4,
            fontSize: 16,
            padding: '6px 12px',
          }} 
        />
      </Form.Item>

      <div style={{ marginLeft: '20px', marginTop: '20px' }}>
        <Form.Item
          name='name_en'
          label='Tên danh mục (tiếng Anh)'
          rules={[{ required: true, message: 'Vui lòng nhập tên danh mục tiếng Anh' }]}
          style={{ marginBottom: '16px' }}
        >
          <Input 
            placeholder='Enter category name in English' 
            style={{
              width: '100%',
              height: 32,
              borderRadius: 4,
              fontSize: 16,
              padding: '6px 12px',
              backgroundColor: '#f5f5f5',
            }} 
          />
        </Form.Item>
      </div>

      <Form.Item name='description' label='Mô tả'>
        <Input.TextArea
          placeholder='Mô tả danh mục'
          rows={3}
          maxLength={200}
          showCount
        />
      </Form.Item>

      <div style={{ marginLeft: '20px', marginTop: '20px' }}>
        <Form.Item
          name='description_en'
          label='Mô tả (tiếng Anh)'
          rules={[{ required: true, message: 'Vui lòng nhập mô tả tiếng Anh' }]}
          style={{ marginBottom: '16px' }}
        >
          <Input.TextArea
            placeholder='Enter category description in English'
            rows={3}
            maxLength={200}
            showCount
            style={{
              backgroundColor: '#f5f5f5',
            }}
          />
        </Form.Item>
      </div>

      <Form.Item
        name='is_visible'
        label='Hiển thị danh mục'
        valuePropName='checked'
        style={{ marginBottom: '16px' }}
      >
        <Switch checkedChildren="Hiện" unCheckedChildren="Ẩn" />
      </Form.Item>

      <Form.Item
        name='image'
        label='Hình ảnh danh mục'
        getValueFromEvent={(e) => {
          if (Array.isArray(e)) {
            return e;
          }
          return imageList[0] || '';
        }}
      >
        <Upload
          listType="picture-card"
          fileList={imageList.map((url: string, index: number) => ({
            uid: `-${index}`,
            name: url.split('/').pop() || `image-${index}`,
            status: 'done',
            url: url,
            thumbUrl: url
          }))}
          customRequest={({ file, onSuccess }: any) => {
            setTimeout(() => {
              onSuccess("ok");
            }, 0);
          }}
          onChange={handleUpload}
          onRemove={handleRemove}
          maxCount={1}
        >
          {imageList.length >= 1 ? null : uploadButton}
        </Upload>
      </Form.Item>
    </Form>
  );
};

export default CategoryForm;
